"use client"

import dynamic from "next/dynamic"
import { useEffect } from "react"
import { useSearchParams } from "next/navigation"
import { useAuthStore } from "@/features/auth/store"
import { useUIStore } from "@/shared/stores/ui-store"

const AuthModals = dynamic(() =>
  import("@/features/auth/components/auth-modals").then(
    (module) => module.AuthModals
  )
)
const SettingsModal = dynamic(() =>
  import("@/app/_components/modals/settings-modal").then(
    (module) => module.SettingsModal
  )
)
const SubscribePaywall = dynamic(() =>
  import("@/features/credits/components/subscribe-paywall").then(
    (module) => module.SubscribePaywall
  )
)
const BuyCoinsPaywall = dynamic(() =>
  import("@/features/credits/components/buy-credits-paywall").then(
    (module) => module.BuyCoinsPaywall
  )
)
const OnboardingModal = dynamic(() =>
  import("@/app/_components/modals/onboarding-modal").then(
    (module) => module.OnboardingModal
  )
)

const AUTH_MODALS = new Set(["login", "register"])

/** Loads modal code only when store or URL state can actually display it. */
export function ProductModalHost() {
  const searchParams = useSearchParams()
  const activeModal = useUIStore((state) => state.activeModal)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const authLoading = useAuthStore((state) => state.isLoading)
  const profile = useAuthStore((state) => state.profile)
  const onboardingRequested = useUIStore((state) => state.onboardingRequested)
  const setOnboardingRequested = useUIStore((state) => state.setOnboardingRequested)

  const hasSettingsUrlState =
    searchParams.has("settings") ||
    searchParams.has("success") ||
    searchParams.has("session_id") ||
    searchParams.has("coin_purchase") ||
    searchParams.has("upgraded")
  const hasPaywallUrlState = searchParams.has("paywall")
  const needsOnboarding =
    !authLoading &&
    isAuthenticated &&
    Boolean(profile) &&
    !profile?.onboardingCompleted &&
    (!profile?.username ||
      profile.contentPreferences == null ||
      !profile.tosAccepted)

  useEffect(() => {
    if (!needsOnboarding && onboardingRequested) {
      setOnboardingRequested(false)
    }
  }, [needsOnboarding, onboardingRequested, setOnboardingRequested])

  return (
    <>
      {activeModal && AUTH_MODALS.has(activeModal) ? <AuthModals /> : null}
      {activeModal === "settings" || hasSettingsUrlState ? (
        <SettingsModal />
      ) : null}
      {activeModal === "subscribe-paywall" || hasPaywallUrlState ? (
        <SubscribePaywall />
      ) : null}
      {activeModal === "buy-credits-paywall" ? <BuyCoinsPaywall /> : null}
      {needsOnboarding ? <OnboardingModal forceVisible={onboardingRequested} /> : null}
    </>
  )
}
